/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * shareInbox.js
 *
 * Module-level holder of the ONE pending "Share to Happy" payload, between the
 * moment the OS hands it over (`components/share/ShareIntentHost`) and the
 * moment the share screen has routed it somewhere.
 *
 * Why not route params: a share can arrive while the user is logged out, while
 * the navigator is still mounting, or while it is being re-keyed after login —
 * route params would be lost with the remount. The payload lives here instead;
 * the screen only receives its `shareId` and looks it up, and CLAIMS it once it
 * is on screen so the host stops trying to navigate. Pure JS (unit-tested).
 *
 * @module helpers/share/shareInbox
 */

/** @type {?(import('./sharePayload').SharePayload & {claimed: boolean})} */
let pending = null;

/** @type {Set<Function>} */
const listeners = new Set();

const emit = () => {
    listeners.forEach((listener) => {
        try {
            listener(pending);
        } catch (_error) {
            // A faulty subscriber must never block the others.
        }
    });
};

/**
 * Store a freshly received share (replacing any older, unrouted one — the
 * newest share is the one the user is looking at).
 * @param {?import('./sharePayload').SharePayload} payload Normalized share.
 * @returns {void}
 */
export const setPendingShare = (payload) => {
    pending = payload ? {...payload, claimed: false} : null;
    emit();
};

/**
 * The pending share, if any.
 * @returns {?(import('./sharePayload').SharePayload & {claimed: boolean})}
 */
export const getPendingShare = () => pending;

/**
 * Look a share up by id (the share screen's route param).
 * @param {string} shareId Share id.
 * @returns {?(import('./sharePayload').SharePayload & {claimed: boolean})}
 */
export const getShareById = (shareId) => (pending && shareId && pending.id === shareId ? pending : null);

/**
 * Mark the share as shown by the share screen, so the host stops navigating.
 * @param {string} shareId Share id.
 * @returns {boolean} Whether the id matched the pending share.
 */
export const claimPendingShare = (shareId) => {
    if (!pending || pending.id !== shareId) {
        return false;
    }
    if (!pending.claimed) {
        pending = {...pending, claimed: true};
        emit();
    }

    return true;
};

/**
 * Drop the share once it was routed (or discarded). Passing an id only clears
 * that share, so a newer one that arrived meanwhile survives.
 * @param {string} [shareId] Share id; omit to clear unconditionally.
 * @returns {void}
 */
export const clearPendingShare = (shareId) => {
    if (!pending || (shareId && pending.id !== shareId)) {
        return;
    }
    pending = null;
    emit();
};

/**
 * Subscribe to inbox changes.
 * @param {(share: ?object) => void} listener Called with the new pending share.
 * @returns {() => void} Unsubscribe.
 */
export const subscribePendingShare = (listener) => {
    if (typeof listener !== 'function') {
        return () => {};
    }
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
};
